import React, { createRef, useCallback, useEffect, useRef, useState } from "react";
import _ from "lodash";
import Swiper from "swiper";
import classNames from "classnames";
import { BodyText, Icon, PrimaryButton } from "../../elements";
import RouteModal from "../../sections/route-modal";
import ImageCarouselModal from "../../sections/image-carousel-modal";
import { isArray, isString } from "../../utils/obj-utils";
import { mobileIs } from "../../utils/siteUtils";
import { gtag } from "../../utils";
import "./style.scss";
const ListSwiper = props => {
  const { lang, fields, page } = props;
  const { paddingTop, paddingBottom, overline, title, slides, paginationPosition } = fields;
  const isAR = page?.fields?.rtl?.value;
  const isMobile = mobileIs();
  const [position, setPosition] = useState("bottom-center");
  const [isTablet, setIsTablet] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const top = paddingTop?.fields.phrase.value ?? "none";
  const bottom = paddingBottom?.fields.phrase.value ?? "none";
  const rootNode = useRef(null);
  const swiperNodeRef = useRef(null);
  const modalRef = createRef();
  const routeModalRef = createRef();
  const swiper = createRef();
  const debouncedSlideClick = useCallback(_.debounce(callback => callback(), 50));
  const onSlideClick = data => {
    gtag('event', 'Component_info', {
      'component_name': 'ListSwiper',
      'page_title': page?.name
    });
    debouncedSlideClick(() => {
      if (isString(data)) {
        routeModalRef.current.open(data, props);
      }
      if (isArray(data)) {
        const index = isAR ? data.length - 1 : 0;
        modalRef.current.open(data, index);
      }
    });
  };
  const buildSwiper = () => {
    destroySwiper();
    const _isTablet = window.innerWidth <= 992;
    const _position = _isTablet ? "bottom-center" : paginationPosition?.fields.phrase.value ?? "bottom-center";
    setPosition(_position);
    setIsTablet(_isTablet);
    const options = {
      slidesPerView: 3,
      spaceBetween: 30
    };
    let _pageVisible = true;
    if (_isTablet) {
      _pageVisible = slides.length > 1;
      Object.assign(options, {
        slidesPerView: "auto",
        spaceBetween: 15,
        initialSlide: isAR? slides.length : 0
      });
    } else {
      _pageVisible = slides.length > 3;
      Object.assign(options, {
        slidesPerGroup: 3
      });
    }
    if (_pageVisible) {
      Object.assign(options, {
        navigation: {
          nextEl: ".c-list-swiper__left",
          prevEl: ".c-list-swiper__right",
        },
        pagination: {
          el: rootNode.current.querySelectorAll(".c-list-swiper__pagination")
        }
      });
    }
    setPageVisible(_pageVisible);
    if (isAR) {
      Object.assign(options, {
        initialSlide: slides.length - 1
      });
    }
    swiper.current = new Swiper(swiperNodeRef.current, options);
  };
  const destroySwiper = () => {
    if (swiper.current) {
      swiper.current.destroy(true, true);
      swiper.current = null;
    }
  };
  const rootClasses = classNames("c-list-swiper grid", `padding-bottom-${bottom}`, `padding-top-${top}`);
  useEffect(() => {
    buildSwiper();
  }, [page, position]);
  useEffect(() => {
    const onResize = () => {
      !isMobile && buildSwiper();
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      destroySwiper();
    };
  }, []);
  return (
    <div className={rootClasses} ref={rootNode}>
      <div className="c-list-swiper__main">
        <BodyText html={overline} className="c-list-swiper__overline" />
        <div className="c-list-swiper__header">
          <BodyText html={title} className="c-list-swiper__title" />
          {position === "top-right" && pageVisible && (
            <div className="c-list-swiper__nav">
              <Icon name="ArrowBack" className="c-list-swiper__left" />
              <div className="c-list-swiper__pagination" />
              <Icon name="ArrowForward" className="c-list-swiper__right" />
            </div>
          )}
        </div>
        <div className="swiper-container" ref={swiperNodeRef}>
          <div className="swiper-wrapper">
            {slides.map(slide => (
              <ListSwiperSlide {...slide} key={slide.id} onClick={onSlideClick} />
            ))}
          </div>
        </div>
        {position === "bottom-center" && pageVisible && (
          <div className="c-list-swiper__nav">
            {!isTablet && <Icon name="ArrowBack" className="c-list-swiper__left" />}
            <div className="c-list-swiper__pagination" />
            {!isTablet && <Icon name="ArrowForward" className="c-list-swiper__right" />}
          </div>
        )}
      </div>
      <ImageCarouselModal ref={modalRef} />
      <RouteModal ref={routeModalRef} page={page}/>
    </div>
  );
};

const ListSwiperSlide = props => {
  const { fields, onClick } = props;
  const { image, title, subtitle, description, link, modalLink, modalItems, colorScheme, iconName, buttonText } = fields;
  const btnProps = {
    colorScheme,
    iconName,
    buttonText,
    link
  };
  if (modalItems.length > 0) {
    Object.assign(btnProps, {
      callback() {
        onClick &&
          onClick(
            modalItems.map(x => ({
              image: x.fields.image.value,
              title: x.fields.title.value
            }))
          );
      }
    });
  }
  if (modalLink.value) {
    Object.assign(btnProps, {
      callback() {
        onClick && onClick(modalLink.value.replace(/^.*\/home/gi, ""));
      }
    });
  }
  return (
    <div className="swiper-slide show-on-scroll">
      <img className="slide-img" alt="" {...image.value} />
      <BodyText html={title} className="slide-title" tagName="div" />
      <BodyText html={subtitle} className="slide-subtitle" tagName="div" />
      <BodyText html={description} className="slide-desc" tagName="div" />
      <div className="slide-btn">{(link?.value.href || modalItems.length > 0 || modalLink.value) && <PrimaryButton {...btnProps} />}</div>
    </div>
  );
};
export default ListSwiper;
